import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../button";
import styles from "./style.module.css";

interface DeleteDeviceModalProps {
  open: boolean;
  deviceName: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Подтверждение удаления устройства. По аналогии с link-telegram-modal /
 * logout-confirm-modal: bottom sheet на мобилке, центр экрана на ≥768px.
 */
function DeleteDeviceModal({
  open,
  deviceName,
  onClose,
  onConfirm,
  loading = false,
}: DeleteDeviceModalProps) {
  const [animatedOpen, setAnimatedOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setAnimatedOpen(false);
      return;
    }
    const frame = requestAnimationFrame(() => setAnimatedOpen(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, loading, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`${styles.overlay} ${animatedOpen ? styles.open : ""}`}
      role="presentation"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-device-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 id="delete-device-modal-title" className={styles.title}>
            Удалить устройство {deviceName}?
          </h2>
          <p className={styles.subtitle}>
            Отключим его от вашей подписки. Чтобы вернуть доступ, вам потребуется заново
            подключить его, используя инструкцию.
          </p>
        </div>
        <div className={styles.actions}>
          <Button
            label="Удалить устройство"
            onClick={onConfirm}
            disabled={loading}
            loading={loading}
          />
          <Button
            label="Отмена"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DeleteDeviceModal;
